/**
 * events-ga4.js
 * Capa central de eventos GA4 para Shopify + Google Tag Manager.
 *
 * Instalación:
 * 1) Sube este archivo en Shopify > Online Store > Themes > Edit code > Assets.
 * 2) Cárgalo antes de las secciones que llamen window.GA4Events:
 *    {{ 'events-ga4.js' | asset_url | script_tag }}
 * 3) En GTM crea triggers de Custom Event usando los nombres:
 *    view_promotion, select_promotion, add_to_cart, shoplook_product_toggle,
 *    shoplook_pin_click, shoplook_variant_change, shoplook_add_to_cart_error.
 */

(function (window, document) {
  'use strict';

  if (window.GA4Events && window.GA4Events.version) return;

  window.dataLayer = window.dataLayer || [];

  const VERSION = '1.0.0';
  const viewedOnce = new Set();

  function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function centsToMoney(cents) {
    return Number((parseInt(cents || '0', 10) / 100).toFixed(2));
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function textFrom(root, selector) {
    const element = root ? root.querySelector(selector) : null;
    return cleanText(element ? element.textContent : '');
  }

  function getActiveVariantText(card) {
    const select = card ? card.querySelector('[data-variant-select]') : null;
    if (!select || !select.options || select.selectedIndex < 0) return '';

    const selectedOption = select.options[select.selectedIndex];
    return cleanText(
      selectedOption.dataset.variantTitle ||
      selectedOption.textContent ||
      ''
    );
  }

  function pushToDataLayer(eventName, ecommerceData, extraData) {
    const eventPayload = {
      event: eventName
    };

    if (ecommerceData) {
      window.dataLayer.push({ ecommerce: null });
      eventPayload.ecommerce = ecommerceData;
    }

    if (extraData && typeof extraData === 'object') {
      Object.assign(eventPayload, extraData);
    }

    window.dataLayer.push(eventPayload);
    return eventPayload;
  }

  function getShopLookContext(slide) {
    if (!slide) return null;

    const section = slide.closest('[data-ga4-section]') || slide.closest('.shop-look-slider');
    const lookId = cleanText(slide.dataset.lookId || '');
    const slideIndex = toNumber(slide.dataset.slideIndex, 0);
    const sectionTitle = cleanText(section ? section.dataset.sectionTitle : '');
    const lookTitle = cleanText(
      slide.dataset.lookTitle ||
      textFrom(slide, '.shop-look-slider__look-title') ||
      textFrom(slide, '.shop-look-slider__products-title') ||
      lookId ||
      'Shop the Look'
    );

    return {
      section_id: cleanText(section ? section.dataset.sectionId : ''),
      section_name: sectionTitle || 'Shop the Look Slider',
      currency: cleanText(section ? section.dataset.currency : '') || 'COP',
      look_id: lookId,
      look_name: lookTitle,
      look_discount: toNumber(slide.dataset.lookDiscount, 0),
      promotion_id: cleanText(slide.dataset.promotionId || `shoplook_${lookId || slideIndex + 1}`),
      promotion_name: lookTitle,
      creative_name: 'Shop the Look Slider',
      creative_slot: `look_${slideIndex + 1}`,
      slide_index: slideIndex + 1
    };
  }

  function buildShopLookItem(card, context, index) {
    if (!card || !context) return null;

    const checkbox = card.querySelector('[data-product-checkbox]');
    if (!checkbox) return null;

    const title = cleanText(
      checkbox.dataset.productTitle ||
      card.dataset.productTitle ||
      textFrom(card, '.shop-look-slider__product-title') ||
      `Product ${index + 1}`
    );

    const price = centsToMoney(checkbox.dataset.productPrice);
    const comparePrice = centsToMoney(checkbox.dataset.productComparePrice);
    const variantText = cleanText(
      checkbox.dataset.productVariantTitle ||
      getActiveVariantText(card)
    );

    const itemId = cleanText(
      checkbox.dataset.productSku ||
      checkbox.dataset.productId ||
      checkbox.dataset.productVariantId ||
      card.dataset.productId ||
      ''
    );

    return {
      item_id: itemId,
      item_name: title,
      item_variant: variantText,
      item_brand: cleanText(checkbox.dataset.productVendor || card.dataset.productVendor || ''),
      item_category: cleanText(checkbox.dataset.productType || card.dataset.productType || ''),
      item_list_id: context.look_id,
      item_list_name: context.look_name,
      promotion_id: context.promotion_id,
      promotion_name: context.promotion_name,
      creative_name: context.creative_name,
      creative_slot: context.creative_slot,
      price: price,
      discount: comparePrice > price ? Number((comparePrice - price).toFixed(2)) : 0,
      quantity: 1,
      index: index + 1
    };
  }

  function getShopLookItems(slide, onlySelected) {
    if (!slide) return [];

    const context = getShopLookContext(slide);
    if (!context) return [];

    const cards = Array.from(slide.querySelectorAll('[data-product-card]'));

    return cards
      .map((card, index) => {
        const checkbox = card.querySelector('[data-product-checkbox]');
        if (!checkbox) return null;
        if (onlySelected && !checkbox.checked) return null;
        return buildShopLookItem(card, context, index);
      })
      .filter(Boolean);
  }

  function getSelectedValue(slide) {
    return getShopLookItems(slide, true).reduce((sum, item) => {
      return sum + toNumber(item.price, 0) * toNumber(item.quantity, 1);
    }, 0);
  }

  function getSelectedCount(slide) {
    return getShopLookItems(slide, true).length;
  }

  const GA4Events = {
    version: VERSION,

    push(eventName, payload) {
      window.dataLayer.push({
        event: eventName,
        ...(payload || {})
      });
    },

    ecommerce(eventName, ecommerceData, extraData) {
      return pushToDataLayer(eventName, ecommerceData, extraData);
    },

    custom(eventName, params) {
      return pushToDataLayer(eventName, null, params || {});
    },

    helpers: {
      centsToMoney,
      cleanText,
      getShopLookContext,
      getShopLookItems,
      getSelectedValue,
      getSelectedCount
    },

    shopLook: {
      viewPromotion(slide, options) {
        const context = getShopLookContext(slide);
        if (!context) return false;

        const dedupeKey = [
          'view_promotion',
          context.section_id,
          context.look_id,
          window.location.pathname
        ].join('|');

        const shouldDedupe = !options || options.once !== false;
        if (shouldDedupe && viewedOnce.has(dedupeKey)) return false;

        const items = getShopLookItems(slide, false);
        if (!items.length) return false;

        if (shouldDedupe) viewedOnce.add(dedupeKey);

        return pushToDataLayer('view_promotion', {
          currency: context.currency,
          promotion_id: context.promotion_id,
          promotion_name: context.promotion_name,
          creative_name: context.creative_name,
          creative_slot: context.creative_slot,
          items: items
        }, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_discount: context.look_discount,
          shoplook_section_id: context.section_id,
          shoplook_slide_index: context.slide_index
        });
      },

      selectPromotion(slide) {
        const context = getShopLookContext(slide);
        if (!context) return false;

        const selectedItems = getShopLookItems(slide, true);
        if (!selectedItems.length) return false;

        return pushToDataLayer('select_promotion', {
          currency: context.currency,
          value: Number(getSelectedValue(slide).toFixed(2)),
          promotion_id: context.promotion_id,
          promotion_name: context.promotion_name,
          creative_name: context.creative_name,
          creative_slot: context.creative_slot,
          items: selectedItems
        }, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_discount: context.look_discount,
          shoplook_section_id: context.section_id,
          selected_items_count: selectedItems.length
        });
      },

      addToCart(slide) {
        const context = getShopLookContext(slide);
        if (!context) return false;

        const selectedItems = getShopLookItems(slide, true);
        if (!selectedItems.length) return false;

        return pushToDataLayer('add_to_cart', {
          currency: context.currency,
          value: Number(getSelectedValue(slide).toFixed(2)),
          items: selectedItems
        }, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_discount: context.look_discount,
          shoplook_section_id: context.section_id,
          selected_items_count: selectedItems.length
        });
      },

      productToggle(checkbox) {
        if (!checkbox) return false;

        const slide = checkbox.closest('[data-slide]');
        const card = checkbox.closest('[data-product-card]');
        const context = getShopLookContext(slide);
        if (!slide || !card || !context) return false;

        const cards = Array.from(slide.querySelectorAll('[data-product-card]'));
        const itemIndex = Math.max(cards.indexOf(card), 0);
        const item = buildShopLookItem(card, context, itemIndex);

        return pushToDataLayer('shoplook_product_toggle', null, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_section_id: context.section_id,
          product_selected: Boolean(checkbox.checked),
          selected_items_count: getSelectedCount(slide),
          item_id: item ? item.item_id : cleanText(checkbox.dataset.productId),
          item_name: item ? item.item_name : cleanText(checkbox.dataset.productTitle),
          item_variant: item ? item.item_variant : cleanText(checkbox.dataset.productVariantTitle),
          price: item ? item.price : centsToMoney(checkbox.dataset.productPrice)
        });
      },

      pinClick(pin) {
        if (!pin) return false;

        const slide = pin.closest('[data-slide]');
        const context = getShopLookContext(slide);
        if (!context) return false;

        return pushToDataLayer('shoplook_pin_click', null, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_section_id: context.section_id,
          product_card_id: cleanText(pin.dataset.scrollTo || pin.dataset.productPin || '')
        });
      },

      variantChange(select) {
        if (!select) return false;

        const slide = select.closest('[data-slide]');
        const card = select.closest('[data-product-card]');
        const checkboxId = select.dataset.productCheckboxId;
        const checkbox = checkboxId ? document.getElementById(checkboxId) : null;
        const context = getShopLookContext(slide);
        if (!slide || !card || !context) return false;

        const selectedOption = select.options[select.selectedIndex];
        const itemName = cleanText(
          card.dataset.productTitle ||
          textFrom(card, '.shop-look-slider__product-title')
        );

        return pushToDataLayer('shoplook_variant_change', null, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_section_id: context.section_id,
          item_id: cleanText(selectedOption ? selectedOption.value : ''),
          item_name: itemName,
          item_variant: cleanText(
            selectedOption
              ? selectedOption.dataset.variantTitle || selectedOption.textContent
              : ''
          ),
          product_selected: checkbox ? Boolean(checkbox.checked) : false,
          price: selectedOption ? centsToMoney(selectedOption.dataset.price) : 0
        });
      },

      addToCartError(slide, errorMessage) {
        const context = getShopLookContext(slide);
        if (!context) return false;

        return pushToDataLayer('shoplook_add_to_cart_error', null, {
          ga4_source: 'events-ga4.js',
          shoplook_id: context.look_id,
          shoplook_name: context.look_name,
          shoplook_section_id: context.section_id,
          error_message: cleanText(errorMessage || 'unknown_error')
        });
      }
    }
  };

  window.GA4Events = GA4Events;

  window.ShopLookTracking = GA4Events.shopLook;
})(window, document);